import Axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosPromise,
  type AxiosRequestConfig,
  type CancelToken,
  type CancelTokenSource,
} from 'axios'
import type { ICancelable } from '@/services/types'

type RequestInterceptor = (config: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>

export class ApiService {
  private transport: AxiosInstance = Axios.create({
    baseURL: import.meta.env.VUE_APP_API_URL,
  })

  public isApiError(error: unknown): error is AxiosError {
    return Axios.isAxiosError(error)
  }

  public isForbidenError(error: unknown): error is AxiosError {
    return this.isApiError(error) && error.response?.status === 403
  }

  public isNotFoundError(error: unknown): error is AxiosError {
    return this.isApiError(error) && error.response?.status === 404
  }

  public isBadRequestError(error: unknown): error is AxiosError {
    return this.isApiError(error) && error.response?.status === 400
  }

  public isCancel(error: unknown) {
    return Axios.isCancel(error)
  }

  public registerRequestInterceptor(interceptor: RequestInterceptor) {
    return this.transport.interceptors.request.use(interceptor)
  }

  public get<T, P extends AxiosRequestConfig = AxiosRequestConfig>(url: string, config?: P): AxiosPromise<T> {
    return this.transport.get<T>(url, config)
  }

  public post<T, B = Record<string, any>>(url: string, data?: B, config?: AxiosRequestConfig): AxiosPromise<T> {
    return this.transport.post(url, data, config)
  }

  protected getCancelToken(): CancelTokenSource {
    return Axios.CancelToken.source()
  }

  protected makeCancelableRequest = <Response>(fn: (cancelToken: CancelToken) => AxiosPromise<Response>) => {
    const cancelToken = this.getCancelToken()
    const promise: AxiosPromise<Response> & ICancelable = fn(cancelToken.token) as any
    promise.cancel = () => cancelToken.cancel()
    return promise
  }
}

export default new ApiService()
